import Vue from "vue";
import { FluxCMS } from "@/api/FluxCMS";
import {
  Prefab,
  PrefabBuilderInstance,
  SituationImage,
} from "./PrefabDataTypes";
import { EventBuilderInstance } from "./EventDataTypes";
import { User } from "./UserDataTypes";
import MediaManager from "@/api/MediaManager";
import Converter from "@/components/babylonjs/Converter";
import { SoundTrack } from "babylonjs";
import axios from "axios";
import {
  DeleteMessage,
  CommandComeTo,
  CommandBuilderInstance,
} from "./CommandDataTypes";
import ImageSphere from "../../components/flux360/components/ImageSphere";
import { PanoramaScene } from "../../components/flux360/PanoramaScene";

const Flux360Store = {
  state: {
    flux360Version: process.env.VUE_APP_VERSION || "0.0.0",
    // Configuration of the Flux360 Tour.
    // These are the defaults set in the application.
    // Defaults will be overwritten by a file flux_config.json on the server
    config: {
      sessionID: undefined, // ID to use for the video session, if undefined, use tour ID
      panoramaTourZIP: undefined,
      panoramaTourZIPEN: undefined,
      showLanguageSelection: false,
      enableWatermark: true,

      logoURL: "/Evonik360/Evonik-Logo.png",
      enableCommunication: false,
      enableBugReport: true,
      enableZooming: false,
      enablePermanentPanoramaVideoControls: false,
      secondaryColor: "#a34400",
      primaryColor: "#E76101",
      fontFamily: "Avenir",
      enableMap: true,
      enablePanoramaVideoAutoplay: true,
      enableSharing: true,
      mapURL: null,
      mapPins: [],
      favIcon: "/favicon.ico",

      websiteTitle: "Evonik°",
      enableWaitForModerator: false,
      roomRotations: [],
      thumbnailType: "jpg",
      roomOrder: null,
      cameraZoom: 0.9,
      enablePreloadAllSituations: false,
      chosenLanguage: null,
      withSubtitles: false,
      externeMediathekLinks: [],
      mediathek: "",
    },

    converter: Converter,
    prefabs: [],
    users: [],
    events: [],
    commands: [],
    localUserID: undefined,
    media: MediaManager,
    inSetup: false,
    vrMode: false,
    gyrosActive: false,
    gyrosSupported: false,
    sounds: SoundTrack,
    overwriteStartSituationID: undefined, // If this is set to a certain ID, it will overwrite the ID from the CMS
    doRendering: true,
    isDialogueOpen: false,
    loadingscreenVisible: 0,
    loadingScreenWanted: true,
    moderatorMode: false,
    stopModeratorModeSignals: false,

    /*** Settings for publisher ***/
    audioDeviceId: undefined,
    videoDeviceId: undefined,
    audioOn: false,
    videoOn: false,
    framerate: 30,
    audiobitrate: undefined,
    audioProcessingDeactivated: false,
    blockSitutationTransition: false,
  },
  getters: {
    mediathek: (state) => {
      return state.config.mediathek;
    },
    externeMediathekLinks: (state) => {
      return state.config.externeMediathekLinks;
    },
    appVersion: (state) => {
      return state.flux360Version;
    },
    panoramaTour: (state) => {
      const apps = state.prefabs.filter((x) =>
        x.isOfType(Prefab.PrefabType.Application360)
      );
      if (apps.length == 1) return apps[0];
      else console.error("No App defined!");
    },
    currentSituationID: (state, getters) => {
      return getters.localUser
        ? getters.localUser.getCurrentSituationID()
        : undefined;
    },

    situations: (state) => {
      return state.prefabs.filter((x) =>
        x.isOfType(Prefab.PrefabType.Situation)
      );
    },
    users: (state) => {
      return state.users;
    },

    events: (state) => {
      return state.events;
    },

    commands: (state) => {
      return state.commands;
    },
    localUserID: (state) => {
      return state.localUserID;
    },
    currentSituation: (state, getters) => {
      return state.prefabs.find(
        (x) => x.getID() === getters.currentSituationID
      );
    },
    currentPrefabs: (state, getters) => {
      return state.prefabs.filter(
        (x) => x.getSituation && x.getSituation() === getters.currentSituationID
      );
    },
    prefabsForSituation: (state) => (situationID) => {
      return state.prefabs.filter(
        (x) => x.getSituation && x.getSituation() === situationID
      );
    },
    remoteUsersInCurrentSituation: (state, getters) => {
      return state.users.filter(
        (x) =>
          x.getCurrentSituationID() === getters.currentSituationID &&
          x.getID() !== state.localUserID
      );
    },
    localUser: (state) => {
      return state.users.find((x) => x.getID() === state.localUserID);
    },
    allPrefabs: (state) => {
      return state.prefabs;
    },
    moderatorInSession: (state) => {
      return (
        state.users.find((user) => user.getRole() == "moderator") != undefined
      );
    },
    panoramaTourZIP: (state) => {
      return state.config.panoramaTourZIP;
    },
    panoramaTourZIPEN: (state) => {
      return state.config.panoramaTourZIPEN;
    },
    showLanguageSelection: (state) => {
      return state.config.showLanguageSelection;
    },
    roomOrder: (state) => {
      return state.config.roomOrder;
    },
    cameraZoom: (state) => {
      return state.config.cameraZoom;
    },
    thumbnailType: (state) => {
      return state.config.thumbnailType;
    },
    roomRotations: (state) => {
      return state.config.roomRotations;
    },
    enableWaitForModerator: (state) => {
      return state.config.enableWaitForModerator;
    },
    stopModeratorModeSignals: (state) => {
      return state.stopModeratorModeSignals;
    },
    moderatorMode: (state) => {
      return state.moderatorMode;
    },
    enableSharing: (state) => {
      return state.config.enableSharing;
    },
    isDialogueOpen: (state) => {
      return state.isDialogueOpen;
    },
    doRendering: (state) => {
      return state.doRendering;
    },
    loadingscreenVisible: (state) => {
      return state.loadingscreenVisible;
    },
    loadingScreenWanted: (state) => {
      return state.loadingScreenWanted;
    },
    fontFamily: (state) => {
      return state.config.fontFamily;
    },
    mapURL: (state) => {
      return state.config.mapURL;
    },
    favIcon: (state) => {
      return state.config.favIcon;
    },
    websiteTitle: (state) => {
      return state.config.websiteTitle;
    },
    mapPins: (state) => {
      return state.config.mapPins;
    },
    enablePanoramaVideoAutoplay: (state) => {
      return state.config.enablePanoramaVideoAutoplay;
    },
    enableMap: (state) => {
      return state.config.enableMap;
    },
    primaryColor: (state) => {
      return state.config.primaryColor;
    },
    secondaryColor: (state) => {
      return state.config.secondaryColor;
    },
    enableZooming: (state) => {
      return state.config.enableZooming;
    },
    enablePermanentPanoramaVideoControls: (state) => {
      return state.config.enablePermanentPanoramaVideoControls;
    },
    enableWatermark: (state) => {
      return state.config.enableWatermark;
    },
    logoURL: (state) => {
      return state.config.logoURL;
    },
    enableCommunication: (state) => {
      return state.config.enableCommunication;
    },
    enableBugReport: (state) => {
      return state.config.enableBugReport;
    },
    isVRMode: (state) => {
      return state.vrMode;
    },
    isInSetup: (state) => {
      return state.inSetup;
    },
    getAudioDeviceId: (state) => {
      return state.audioDeviceId;
    },
    getVideoDeviceId: (state) => {
      return state.videoDeviceId;
    },
    isAudioOn: (state) => {
      return state.audioOn;
    },
    isVideoOn: (state) => {
      return state.videoOn;
    },
    getFramerate: (state) => {
      return state.framerate;
    },
    getAudiobitrate: (state) => {
      return state.audiobitrate;
    },
    isAudioProcessingDeactivated: (state) => {
      return state.audioProcessingDeactivated;
    },
    isGyrosActive: (state) => {
      return state.gyrosActive;
    },
    isGyrosSupported: (state) => {
      return state.gyrosSupported;
    },
    enablePreloadAllSituations: (state) => {
      return state.config.enablePreloadAllSituations;
    },
    chosenLanguage: (state) => {
      return state.config.chosenLanguage;
    },
    withSubtitles: (state) => {
      return state.config.withSubtitles;
    },
  },
  mutations: {
    setExterneMediathekLinks(state, data) {
      state.config.externeMediathekLinks = data;
    },
    removeSituation(state, situationID) {
      let situation = state.prefabs.find(
        (x) => x.getID() === getters.situationID
      );
      var index = state.prefabs.indexOf(situation);
      if (index > -1) {
        state.prefabs.splice(index, 1);
      }
    },

    setMapURL(state, value) {
      state.config.mapURL = value;
    },
    setChosenLanguage(state, value) {
      state.config.chosenLanguage = value;
    },
    setWithSubtitles(state, value) {
      state.config.withSubtitles = value;
    },
    setLoadingscreenVisible(state, int) {
      if (int == 0) {
        state.loadingscreenVisible = 0;
      }
      state.loadingscreenVisible += int;
    },
    setLoadingScreenWanted(state, value) {
      state.loadingScreenWanted = value;
    },
    setGyrosSupported: (state) => {
      state.gyrosSupported = state;
    },
    overwriteStartSituationID(state, { situationID } = {}) {
      if (situationID) state.overwriteStartSituationID = situationID;
    },

    setSessionID(state, id) {
      state.config.sessionID = id;
    },

    setBlockSitutationTransition(state, bool) {
      state.blockSitutationTransition = bool;
    },

    setStopModeratorModeSignals(state, bool) {
      state.stopModeratorModeSignals = bool;
    },

    setModeratorMode(state, mode) {
      state.moderatorMode = mode;
    },

    setVRMode(state, mode) {
      state.vrMode = mode;
    },

    setIsInSetup(state, setup) {
      state.inSetup = setup;
    },
    setOpenTokTest(state, status) {
      state.openTokTest = status;
    },
    setOpenTokConnectivityResult(state, result) {
      state.openTokConnectivityResult = result;
    },
    setOpenTokQualityResult(state, result) {
      state.openTokQualityResult = result;
    },
    setOpenTokConnectivityError(state, error) {
      state.openTokConnectivityError = error;
    },
    setOpenTokQualityError(state, error) {
      state.openTokQualityError = error;
    },
    setAudioDeviceId(state, id) {
      state.audioDeviceId = id;
    },
    setVideoDeviceId(state, id) {
      state.videoDeviceId = id;
    },

    setAudioOn(state, boolean) {
      state.audioOn = boolean;
    },

    setVideoOn(state, boolean) {
      state.videoOn = boolean;
    },

    setFramerate(state, number) {
      state.framerate = number;
    },

    setAudiobitrate(state, number) {
      state.audiobitrate = number;
    },

    setAudioProcessingDeactivated(state, number) {
      state.audioProcessingDeactivated = number;
    },

    setPrefabs(state, prefabs) {
      state.prefabs = prefabs;

      const apps = state.prefabs.filter((x) =>
        x.isOfType(Prefab.PrefabType.Application360)
      );
      if (!apps.length) {
        console.warn("No App defined!");
      } else {
        if (state.localUserID) {
          console.log(
            "New panorama tour set, setting local user in starting situation."
          );
          console.log(
            "ID is " +
              apps[0].getID() +
              " start is " +
              apps[0].getStartSituationID()
          );
          let localUser = state.users.find(
            (x) => x.getID() === state.localUserID
          );
          if (!localUser) {
            this.commit("updateUser", { id: state.localUserID });
            localUser = state.users.find(
              (x) => x.getID() === state.localUserID
            );
          }

          if (state.overwriteStartSituationID) {
            // it might be that the overwriteStartSituationID is actually false, so we
            // have to make a sanity check to avoid setting the user in non existing situations

            const sit = state.prefabs.find(
              (x) =>
                x.getID() == state.overwriteStartSituationID &&
                x.isOfType(Prefab.PrefabType.Situation)
            );
            if (sit)
              localUser.setCurrentSituationID(state.overwriteStartSituationID);
            else {
              console.log(
                "Overwrite situation ID is " +
                  state.overwriteStartSituationID +
                  " but this situation is not in the tour, using predefined startID " +
                  apps[0].getStartSituationID()
              );
              localUser.setCurrentSituationID(apps[0].getStartSituationID());
            }
          } else localUser.setCurrentSituationID(apps[0].getStartSituationID());
        }
      }
    },

    setCurrentSituation(state, situation) {
      //console.log("Setting current situation to " + situation )

      const user = state.users.find((x) => x.getID() === state.localUserID);
      const currentSituationID = situation ? situation.id : undefined;
      //console.log("Situation ID is " + currentSituationID );
      //state.currentSituationID = currentSituationID;
      if (user) {
        user.setCurrentSituationID(currentSituationID);
      }
    },

    setLocalUser(state, user) {
      if (user) {
        state.users.push(user);
        state.localUserID = user.getID();
      }
    },

    setLocalUserID(state, id) {
      state.localUserID = id;
    },

    addUser(state, user) {
      //console.info(`Flux360Store::addUser: ${JSON.stringify(user)}`);
      state.users.push(user);
      // If a new user is updated, broadcast our own data to welcome him!

      if (user.getID() !== this.localUserID) {
        console.log("### Welcoming new user...");

        let localUser = state.users.find(
          (x) => x.getID() === state.localUserID
        );
        if (localUser) {
          localUser.setShouldSync(true);
        }
      }
    },

    pauseSound(state, file) {
      console.info("Pausing Sound " + file);
    },
    removeUser(state, id) {
      const user = state.users.find((u) => u.id === id);
      if (user) {
        state.users.splice(state.users.indexOf(user), 1);
      }
    },

    updateRuntimeState(state, data = {}) {
      const {
        id,
        paused,
        muted,
        playing,
        waitingForResume,
        duration,
        currentTime,
        volume,

        percentageLoaded,
      } = data;
      //console.log("updateRuntimeState: " + JSON.stringify(data));

      let prefab = state.prefabs.find((x) => x.getID() === id);
      if (prefab) {
        if ("paused" in data) prefab.runtimeState.paused = paused;
        if ("muted" in data) prefab.runtimeState.muted = muted;
        if ("playing" in data) prefab.runtimeState.playing = playing;
        if ("duration" in data) prefab.runtimeState.duration = duration;
        if ("waitingForResume" in data)
          prefab.runtimeState.waitingForResume = waitingForResume;

        if ("currentTime" in data)
          Vue.set(prefab.runtimeState, "currentTime", currentTime);
        if ("volume" in data) Vue.set(prefab.runtimeState, "volume", volume);
        if ("percentageLoaded" in data)
          Vue.set(prefab.runtimeState, "percentageLoaded", percentageLoaded);
      }
    },

    updateUser(state, data = {}) {
      //console.info(`Flux360Store::updateUser: ${JSON.stringify(data)}`);
      //console.info(`Flux360Store::currentUsers: ${JSON.stringify(state.users)}`);
      const {
        id,
        position,
        rotation,
        gazePosition,
        cursorPosition,
        name,
        color,
        role,
        acronym,
        userIcon,
        cmsID,
        isCMSUser,
        currentSituationID,

        onlineStatus,
      } = data;

      let user = state.users.find((x) => x.getID() === id);
      if (!user) {
        user = new User(data);

        this.commit("addUser", user);
      }
      if ("position" in data) user.setPosition(position);
      if ("rotation" in data) user.setRotation(rotation);
      if ("gazePosition" in data) user.setGazePosition(gazePosition);
      if ("cursorPosition" in data) user.setCursorPosition(cursorPosition);
      if ("name" in data) user.setName(name);
      if ("color" in data) user.setColor(color);
      if ("acronym" in data) user.setAcronym(acronym);
      if ("userIcon" in data) user.setUserIcon(userIcon);
      if ("role" in data) user.setRole(role);
      if ("cmsID" in data) user.setCMSID(cmsID);
      if ("isCMSUser" in data) user.setIsCMSUser(isCMSUser);

      if ("currentSituationID" in data)
        user.setCurrentSituationID(currentSituationID);

      // Getters like 'usersInCurrentSituation' will not trigger because
      // the collection itself has not changed. Therefore the panorama scene
      // will not be updated with this information. To avoid this, we need to
      // trigger an event that changes the collection.

      // Why?
      // state.users.push(state.users.pop());

      // Finally update userData (session store)
      /*
      if (id === state.localUserID) {

        this.commit("updateUserData", user);
        if (name || acronym || color || role || currentSituationID) {
          user.setShouldSync();
        }
      } else {

        if (onlineStatus === "streamCreated") {
          console.log("#### Stream created");

          let localUser = state.users.find(
            (x) => x.getID() === state.localUserID
          );
          localUser.setShouldSync(true);
        }
      }*/
    },

    clearMediaManager(state) {
      state.media.MediaManager.clear();
    },

    addMedia(state, media) {
      state.media.addMedia(media);
    },

    handleGotoSituationTransition(
      state,
      { situationID, rotation, comesFromModerator = false } = {}
    ) {
      if (!state.blockSitutationTransition || comesFromModerator) {
        const user = state.users.find((x) => x.getID() === state.localUserID);
        if (user) {
          if (!(rotation === "undefined" || rotation === undefined)) {
            console.log(
              "handleGotoSituationTransition --  setting Rotation: " +
                JSON.stringify(rotation)
            );
            user.setRotation(rotation);
          } else user.setCurrentSituationID(situationID);

          /*
            console.log(
              "handleGotoSituationTransition --  no rotation update requested"
            );
            */
        }
      }
    },

    postEvent(state, event) {
      console.info(`Flux360Store::postEvent: ${JSON.stringify(event)}`);
      // Check if it is a string...
      let e = event;

      if (typeof e === "string") {
        e = JSON.parse(event);

        /*
        console.log(
          "Converted Event to JSON from String: " + JSON.stringify(e)
        );
        */
      }

      if (!e.getType) {
        e = EventBuilderInstance.createFromJSON(e);

        /*
        console.log(
          "Converted Event to Eventclass from JSON: " + JSON.stringify(e)
        );
        */
      }
      state.events.push(e);

      if (e.userID === state.localUserID) {
        // If this is a local user event, let the others now

        this.dispatch("broadcastPostEvent", e);
      }
    },

    removeEventByID(state, { id }) {
      const event = state.events.find((u) => u.getID() === id);
      if (event) {
        state.events.splice(state.events.indexOf(event), 1);
      }
    },

    activateGyros(state, value) {
      state.gyrosActive = value;
    },

    doRendering(state, value) {
      state.doRendering = value;
    },

    setDialogueOpen(state, value) {
      state.isDialogueOpen = value;
    },
    ///***********************************************************************************/
    /// Command Handling

    ///***********************************************************************************/
    postCommand(state, command) {
      console.info(`Flux360Store::postCommand: ${JSON.stringify(command)}`);
      // Check if it is a string...
      let c = command;
      //console.log(typeof c);

      if (typeof c === "string") {
        c = JSON.parse(command);
        //console.log("Converted Command to JSON from String: " + JSON.stringify(c));
      }

      if (!c.getType) {
        c = CommandBuilderInstance.createFromJSON(c);
        //console.log("Converted Command to CommandClass from JSON: " + JSON.stringify(c));
      }
      state.commands.push(c);
    },
  },
  actions: {
    ///***********************************************************************************/
    /// Moderator Commands

    ///***********************************************************************************/
    requestUsersToMe(context, { targetRotation }) {
      // This function is only allowed if the moderator is calling...

      if (
        context.getters.localUser &&
        context.getters.localUser.getRole() === "moderator"
      ) {
        console.log("Local moderator calls all users to come.");

        let localUser = context.getters.localUser;

        let command = new CommandComeTo({
          sourceUserID: localUser.getID(),
          situationID: localUser.getCurrentSituationID(),
          position: localUser.getPosition(),
          rotation: targetRotation,
        });

        //console.log("Broadcasting " + JSON.stringify(command));

        this.dispatch("broadcastCommand", command);
      } else {
        console.log("Normal users cannot call all users.");
      }
    },
    deleteMessage(context, messageToDelete) {
      // This function is only allowed if the moderator is calling...
      //if( context.getters.localUser && context.getters.localUser.getRole() === "moderator" ) {

      console.log("Local moderator calls all users to delete a message.");

      let localUser = context.getters.localUser;

      let command = new DeleteMessage({
        sourceUserID: localUser.getID(),
        message: messageToDelete,
        type: "deleteMessage",
      });

      //console.log("Broadcasting " + JSON.stringify(command));
      this.dispatch("broadcastCommand", command);

      //} else {
      //  console.log("Normal users cannot call all users to delete a message.");
      //}
    },
    ///***********************************************************************************/
    /// User Creation

    ///***********************************************************************************/
    createLocalUser(context) {
      const u = new User({ name: "Hiro Protagonist", acronym: "HP" });
      context.commit("addUser", u);
      const id = u.getID();

      context.commit("setLocalUserID", id);
      if (context.getters.panoramaTour) {
        var currentSituationID =
          context.getters.panoramaTour.getStartSituationID();
        if (context.state.overwriteStartSituationID)
          currentSituationID = context.state.overwriteStartSituationID;

        context.commit("updateUser", { id, currentSituationID });
      }
      // Update session store

      //context.commit("updateUserData", u);
    },

    addTestUser(context) {
      const u = new User();

      if (context.getters.localUser) {
        let localUser = context.getters.localUser;

        u.setPosition(localUser.getPosition());
        u.setGazePosition(localUser.getGazePosition());
        u.setCursorPosition(localUser.getCursorPosition());
        u.setRotation(localUser.getRotation());
        u.setCurrentSituationID(localUser.getCurrentSituationID());
        u.setName("User" + Math.floor(Math.random() * 100));
        let r = 0;
        let g = 0;
        let b = 0;
        while (r + g + b == 0 || r + g + b < 300) {
          r = Math.floor(Math.random() * 255).toString(16);
          g = Math.floor(Math.random() * 255).toString(16);
          b = Math.floor(Math.random() * 255).toString(16);
        }
        if (r.length == 1) r = ("0" + r).toString(16);
        if (g.length == 1) g = ("0" + g).toString(16);
        if (b.length == 1) b = ("0" + b).toString(16);
        u.setColor("#" + r + g + b);
        console.log(
          "Adding user with position and rotation: " +
            JSON.stringify(u.getPosition()) +
            " / " +
            JSON.stringify(u.getRotation())
        );
        console.log("And color " + u.getColor());
      }
      context.commit("addUser", u);
    },
    ///***********************************************************************************/
    /// Startup and Loading Functions

    ///***********************************************************************************/
    async loadLocalConfiguration(context) {
      console.log("FluxCMS::loadingLocalConfiguration");

      return axios
        .get("./flux_config.json")
        .then((response) => {
          /*console.log(
            "Found local config with parameters " +
              JSON.stringify(response.data)
          );*/
          const config = response.data;
          //console.log(config);
          //console.log("########");
          Object.assign(context.state.config, config);
        })
        .catch(function (error) {
          // handle error
          console.log(error);
        });
    },
    // Loads a Flux360 panorama tour from ZIP

    async loadPanoramaTour(context, { zip, preview }) {
      console.log("Loading panorama tour with ID " + zip);
      const fluxCMS = new FluxCMS();
      let prefabs = [];
      let name = "unknown";
      let packageName = "unknown";
      let description = "unknown";

      /*
      // Load the ZIP file
      console.log("FluxCMS::loading...");
      const zipFile = await fluxCMS.getZIP(zip, preview);
      //console.log("FluxCMS::looping files...");
      // Iterate through all files

      const filenames = Object.keys(zipFile.files).sort();
  
      const contents = await Promise.all(
        filenames.map((fileName) => zipFile.file(fileName).async("string"))
      );
      for (let content of contents) {
        const json = JSON.parse(content);
        if (json.idName) {

          // console.log("##########################################")
          // console.log("ID: " + json.idName );
          // console.log("Type: " + json.type);
          // console.log("Content: " + content);
          if (json.type === 30000) {
            const prefab = PrefabBuilderInstance.createFromJSON(json);
            console.log(JSON.stringify(prefab))
            if (prefab)

              // prefabs.push(prefab);
              console.log(".");
          } else {
            const media = MediaManager.createFromJSON(json);

            if (media) {
              // MediaManager.addMedia(media);
            }
          }
        } else {
          if (json.package_name) {
            //console.log("FluxCMS::Parsing application package");
            packageName = json.package_name;
            name = json.package_display_name;
            description = json.package_description;
            //panoramaTour.setContentFileIDs( json.contentFileIds );
            //console.log(content);
          } else {
            console.log("FluxCMS::Unknown content");
            console.log(content);
          }
        }
      }
  */
      //LOAD local file
     
      await axios.get("./media/Evonik360/ExterneMediathekLinks.json").then((response) => {
        let dataJSON = JSON.parse(JSON.stringify(response.data));

        context.commit(
          "setExterneMediathekLinks",
          dataJSON.externeMediathekLinks
        );
      });
      await axios
        .get("./media/Evonik360/Evonik360_Media.json")
        .then((response) => {
          /*console.log(
            "Found local Media file Evonik360 " + JSON.stringify(response.data)
          );
          */

          var table = response.data;
          //console.log(table);
          let tableJSON = JSON.parse(JSON.stringify(table));
          let x = tableJSON.media;
          //console.log(x);

          /*
          for (var key in x) {
            if (table.hasOwnProperty(key)) {
              var val = x[key];
              console.log(val);
              const media = MediaManager.create(val);
              if (media) MediaManager.addMedia(media);
              else console.log("Media Nicht geklappt");

            }
          }*/

          for (let i = 0; i < x.length; i++) {
            //console.log(x[i]);
            const media = MediaManager.create(x[i]);
            if (media) MediaManager.addMedia(media);
            else console.log("Media Nicht geklappt");
          }
        })
        .catch(function (error) {
          // handle error
          console.log(error);
        });

    
      await axios
        .get("./media/Evonik360/Evonik360.json")
        .then((response) => {
          /*
          console.log(
            "Found local Tour file Evonik360 " + JSON.stringify(response.data)
          );
          */

          var table = response.data.table;
          //console.log(table);
          //console.log(table.length);

          table.forEach(function (obj) {
            // console.log(obj.id);
            //  console.log(obj.position);

            //Anpassen  ExterneMediathekLinks

            var adapt = context.getters.externeMediathekLinks.filter(
              (x) => x.defaultFile == obj.url
            );

            if (adapt.length > 0) {
              if (context.getters.mediathek == "alteMediathek") {
                obj.url = adapt[0].alteMediathek;
              } else if (context.getters.mediathek == "neueMediathek") {
                console.log(
                  "Adapting: " + obj.url + " => " + adapt[0].neueMediathek
                );
                obj.url = adapt[0].neueMediathek;
              }
            }

            const prefab = PrefabBuilderInstance.create(obj);
            // console.log(prefab);
            if (prefab) {
              if (prefab.relative_position)
                prefab.setPosition(prefab.relative_position);
              prefabs.push(prefab);
            } else console.log("Nicht geklappt");
          });
        })
        .catch(function (error) {
          // handle error
          console.log(error);
        });

      // See if we have an app
      // to patch missing information into it

      const apps = prefabs.filter((x) =>
        x.isOfType(Prefab.PrefabType.Application360)
      );
      if (apps.length === 1) {
        apps[0].setName(name);
        apps[0].setDescription(description);
        apps[0].setPackageName(packageName);
      }

      prefabs = prefabs.map((x) => {
        //console.log("Checking " + x.getType());
        if (x.isOfType(Prefab.PrefabType.ContentPrefab)) {
          if (x.getPosition()) {
            //console.log("Adapting Position and Rotation");
            //console.log("### Converting "+ x.getPosition().x + " / " + x.getPosition().y + " / " + x.getPosition().z )

            x.setPosition(
              context.state.converter.convertPositionFromCMS(x.getPosition())
            );
            //console.log("### Converted to "+ x.getPosition() )

            x.setRotation(
              context.state.converter.convertRotationFromCMS(x.getRotation())
            );
          }
        }
        return x;
      });

      prefabs = prefabs.map((x) => {
        if (x.isOfType(Prefab.PrefabType.Transition)) {
          if (x.getImageID() === undefined)
            x.setImageID(MediaManager.defaultTransitionTextureID);

          x.setTargetRotation(
            context.state.converter.convertRotationFromCMS(
              x.getTargetRotation()
            )
          );
        }
        return x;
      });
      await MediaManager.resolveContent(fluxCMS);

      //console.log(prefabs);
      context.commit("setPrefabs", prefabs);

      // Let session store know.
      const tourId = context.getters.panoramaTour.id;
      // Here we are setting the ID to use for the video-conferencing session...

      if (context.state.config.sessionID) {
        console.log(
          "Session ID has been set by config to " +
            context.state.config.sessionID
        );
        await this.dispatch(
          "setupDefaultChatRooms",
          context.state.config.sessionID
        );
      } else {
        console.log("Session ID is set to the tour ID " + tourId);

        await this.dispatch("setupDefaultChatRooms", tourId);
      }
      // Initialize User
      await this.dispatch("createLocalUser");

      //JSON Output generieren

      //console.log("JSON Output::: ");
      var obj = {
        table: [],
      };
      prefabs = prefabs.map((x) => {
        //console.log(x.toJSON())
        obj.table.push(x.toJSON());
      });
      //var json = JSON.stringify(obj);

      // console.log(json)

      //  console.log("Media:::")
      //  console.log(JSON.stringify(MediaManager.toJSON()))
    },
  },
};

export default Flux360Store;
